/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useSetRecoilState } from "recoil";
import { IDeleteManyTagsResponse, TagApi } from "../../../api/Tags";
import { TagsMoreActionsPopOverLoadingAtom } from "../../../atoms/MoreActionsPopoverAtom";
import {
  SelectedTagsAtom,
  SelectedTagsBatchDeleteAtom,
} from "../../../atoms/SelectedTags";
import { IsCheckedAllTagsAtom, TagsAtom } from "../../../atoms/TagsAtom";
import useClickOutside from "../../../hook/useClickOutside";
import { customTooltip, notSelect } from "../../../styles/general";
import Icon from "../../Icon";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import StyledButton from "../../StyledButton";
import BatchDeleteTagsModal from "./BatchDelete/BatchDeleteTagsModal";

const Container = styled.div`
  position: relative;
  color: #2f2f2f;
  ${notSelect}
  height: 40px;
  width: 40px;
  display: grid;
  place-items: center;
`;

const Popover = styled.div`
  position: absolute;
  background: white;
  z-index: 1002;
  top: 40px;
  height: fit-content;
  width: 200px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: 10px;

  svg,
  img {
    flex-shrink: 0;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  span {
    margin-left: 7px;
  }

  svg {
    fill: #bbb;
  }

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;

    svg {
      fill: #fff;
    }
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 15px;
`;

const pathHover = css`
  :hover {
    svg {
      path {
        fill: #fff;
      }
    }
  }
`;

interface IActionsPopOver {}

export default function TagBulkActions(props: IActionsPopOver): JSX.Element {
  const [tagsPool, setTagsPool] = useRecoilState(TagsAtom);
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [selectedTags, setSelectedTags] = useRecoilState(SelectedTagsAtom);
  const setIsLoading = useSetRecoilState<boolean>(
    TagsMoreActionsPopOverLoadingAtom
  );
  const setSelectedTagsBatchDelete = useSetRecoilState(
    SelectedTagsBatchDeleteAtom
  );
  const setIsCheckedAllTags = useSetRecoilState(IsCheckedAllTagsAtom);
  const modal = useModal();

  async function deleteTags(tagIds: string[]) {
    try {
      if (!tagIds.length) {
        modal.popModal();
        toast("There are no tags eligible to be deleted", {
          type: "error",
        });
        return;
      }

      setIsLoading(true);
      const deleteTagsResponse: IDeleteManyTagsResponse =
        await TagApi.removeMany(tagIds);

      const thereAreTagsDeleted = deleteTagsResponse.deletedTags.length;
      const thereAreTagsUsedInCampaigns =
        deleteTagsResponse.cantBeDeleted.length;

      if (!!thereAreTagsDeleted) {
        const tagsPoolWithoutDeletedOnes = tagsPool.filter(
          (tagFromPool) =>
            !deleteTagsResponse.deletedTags.includes(tagFromPool._id)
        );
        setTagsPool(tagsPoolWithoutDeletedOnes);
        clearSelectedTags();
        toast("Tags deleted!", {
          type: "success",
        });
      }
      if (!!thereAreTagsUsedInCampaigns) {
        toast(
          "Some tags can not be deleted. They are used to trigger/stop a campaign",
          {
            type: "warning",
          }
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
      clearSelectedTags();
      modal.popModal();
    }
  }

  function clearSelectedTags() {
    setSelectedTags([]);
    setSelectedTagsBatchDelete([]);
    setIsCheckedAllTags(false);
  }

  async function handleDelete() {
    const allTagsSelected = selectedTags.map((tag) =>
      tagsPool.find((st) => st._id === tag)
    );

    const userTagsSelected = allTagsSelected.filter(
      (tag) => tag.type !== "system"
    );

    const userTagsSelectedIds = userTagsSelected.map((tag) => tag._id);

    const systemTagsSelected = allTagsSelected.filter(
      (tag) => tag.type === "system"
    );

    const campaignsUsedInTags = await TagApi.getCampaignsUsedInTags(
      userTagsSelectedIds
    );

    const campaignsUsedInTagsSelected = campaignsUsedInTags.filter(
      (campaignUsed) => userTagsSelectedIds.includes(campaignUsed.tagId)
    );

    const tagsCanBeDeleted = campaignsUsedInTagsSelected
      .filter((c) => !c.campaignsUsedIn.length)
      .map((c) => c.tagId);

    setSelectedTagsBatchDelete(tagsCanBeDeleted);
    setOpenDropView(false);
    if (systemTagsSelected.length && !userTagsSelected.length) {
      modal.pushModal(
        <Warning
          icon={"./icons/delete.svg"}
          title="Delete Tags"
          description={
            <div>
              <span>You have only selected system tags.</span>
              <br />
              <span>
                <i>(System tags cannot be deleted)</i>
              </span>
            </div>
          }
          buttonsName={["Ok"]}
          onOkClicked={() => modal.popModal()}
          onCancelClicked={() => modal.popModal()}
        />
      );
    } else {
      if (systemTagsSelected.length) {
        toast("System tags cannot be deleted", {
          type: "info",
        });
      }
      modal.pushModal(
        <BatchDeleteTagsModal
          selectedTags={userTagsSelected}
          deleteTags={(tagIds) => deleteTags(tagIds)}
          campaignsUsedInTagsSelected={campaignsUsedInTagsSelected}
        />
      );
    }
  }

  return (
    <Container ref={wrapperRef}>
      <div
        style={{ cursor: "pointer" }}
        data-tip
        data-for="actions"
        onClick={() => setOpenDropView(!openDropView)}
      >
        <ReactTooltip
          id="actions"
          place="bottom"
          effect="solid"
          globalEventOff="click"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText>More Actions</TooltipText>
        </ReactTooltip>
        <StyledButton
          css={css`
            height: 40px;
            width: 40px;
          `}
          inverted
          onClick={() => setOpenDropView(!openDropView)}
          role="button"
          tabIndex={0}
        >
          <Icon name="threedots" />
        </StyledButton>
      </div>

      {openDropView && (
        <Popover>
          {selectedTags.length > 1 && (
            <>
              <Title>BULK ACTIONS</Title>

              <Row css={pathHover} onClick={() => handleDelete()}>
                <Icon name="delete" width={16} height={16} />
                <span>Delete</span>
              </Row>
            </>
          )}
        </Popover>
      )}
    </Container>
  );
}
